import { useState, useEffect, useRef } from "react";
import { AlertTriangle, RefreshCw, Download, Edit, X, Check } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../utils/apiClient";
import { exportToXlsx } from "../materials/exportXlsx";
import { usesKm, usesHours } from "./readingType";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MismatchReportTab = ({ projectId, equipment, isMasterAdmin }) => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [mismatches, setMismatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMismatch, setEditingMismatch] = useState(null);
  const [editForm, setEditForm] = useState({ log_km: "", log_hr: "", fuel_km: "", fuel_hr: "" });
  const [saving, setSaving] = useState(false);
  const [machineFilter, setMachineFilter] = useState("");
  const visible = machineFilter ? mismatches.filter(m => m.equipment_id === machineFilter) : mismatches;
  const sortedEquipment = [...equipment].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const reqId = useRef(0);

  const fetchMismatches = async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const [logsRes, fuelRes] = await Promise.all([
        apiClient.get(`/equipment/daily-log/${projectId}`),
        apiClient.get(`/equipment/fuel-issues/${projectId}`)
      ]);
      if (id !== reqId.current) return;
      const logs = logsRes.data.filter(l => l.date >= fromDate && l.date <= toDate);
      const fuels = fuelRes.data.filter(f => f.date >= fromDate && f.date <= toDate);

      const results = [];
      const eqMap = Object.fromEntries(equipment.map(e => [e.id, e]));

      // Group by equipment + date
      const logsByKey = {};
      logs.forEach(l => {
        const key = `${l.equipment_id}_${l.date}`;
        if (!logsByKey[key]) logsByKey[key] = [];
        logsByKey[key].push(l);
      });

      const fuelsByKey = {};
      fuels.forEach(f => {
        const key = `${f.equipment_id}_${f.date}`;
        if (!fuelsByKey[key]) fuelsByKey[key] = [];
        fuelsByKey[key].push(f);
      });

      // Find mismatches
      const allKeys = new Set([...Object.keys(logsByKey), ...Object.keys(fuelsByKey)]);
      for (const key of allKeys) {
        const [eqId, date] = key.split("_");
        const eq = eqMap[eqId];
        if (!eq) continue;

        const dayLogs = logsByKey[key] || [];
        const dayFuels = fuelsByKey[key] || [];

        if (dayLogs.length === 0 || dayFuels.length === 0) continue;

        // Compare the latest log with latest fuel
        const log = dayLogs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
        const fuel = dayFuels.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];

        const logKmClose = log.km_end_reading || 0;
        const logHrClose = log.hour_end_reading || 0;
        const fuelKmClose = fuel.closing_km_reading || fuel.current_km_reading || 0;
        const fuelHrClose = fuel.closing_hour_reading || fuel.current_hour_reading || 0;

        const kmDiff = usesKm(eq) ? Math.abs(logKmClose - fuelKmClose) : 0;
        const hrDiff = usesHours(eq) ? Math.abs(logHrClose - fuelHrClose) : 0;

        if (kmDiff > 0 || hrDiff > 0) {
          results.push({
            equipment_id: eqId, equipment_name: eq.name, equipment_reg: eq.equipment_id, date,
            log_km: logKmClose, fuel_km: fuelKmClose, km_diff: kmDiff,
            log_hr: logHrClose, fuel_hr: fuelHrClose, hr_diff: hrDiff,
            log_km_open: log.km_start_reading || 0, log_hr_open: log.hour_start_reading || 0,
            useKm: usesKm(eq), useHr: usesHours(eq), log_id: log.id, fuel_id: fuel.id, fuel_issue_number: fuel.issue_number
          });
        }
      }

      results.sort((a, b) => b.date.localeCompare(a.date) || (b.km_diff + b.hr_diff) - (a.km_diff + a.hr_diff));
      setMismatches(results);
    } catch (e) {
      if (id === reqId.current) toast.error("Failed to fetch data");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  };

  const openEditDialog = (m) => {
    setEditingMismatch(m);
    setEditForm({ log_km: m.log_km.toString(), log_hr: m.log_hr.toString(), fuel_km: m.fuel_km.toString(), fuel_hr: m.fuel_hr.toString() });
    setEditDialogOpen(true);
  };

  const handleSaveFix = async () => {
    if (!editingMismatch) return;
    setSaving(true);
    try {
      const logKm = parseFloat(editForm.log_km) || 0;
      const logHr = parseFloat(editForm.log_hr) || 0;
      const fuelKm = parseFloat(editForm.fuel_km) || 0;
      const fuelHr = parseFloat(editForm.fuel_hr) || 0;

      // Update Daily Log
      await apiClient.put(`/equipment/daily-log/${editingMismatch.log_id}`, {
        km_end_reading: logKm, hour_end_reading: logHr,
        km_used: Math.max(0, logKm - (editingMismatch.log_km_open || 0)),
        hours_used: Math.max(0, logHr - (editingMismatch.log_hr_open || 0))
      });

      // Update Fuel Issue
      await apiClient.put(`/equipment/fuel-issue/${editingMismatch.fuel_id}`, {
        closing_km_reading: fuelKm, closing_hour_reading: fuelHr
      });

      toast.success("Readings updated successfully");
      setEditDialogOpen(false);
      fetchMismatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update readings");
    }
    setSaving(false);
  };

  const copyLogToFuel = () => setEditForm(f => ({ ...f, fuel_km: f.log_km, fuel_hr: f.log_hr }));
  const copyFuelToLog = () => setEditForm(f => ({ ...f, log_km: f.fuel_km, log_hr: f.fuel_hr }));

  useEffect(() => {
    if (equipment.length > 0) fetchMismatches();
  }, [equipment]);

  const handleExport = async () => {
    if (visible.length === 0) return toast.error("No data to export");
    await exportToXlsx({
      fileName: `Reading_Mismatches_${fromDate}_${toDate}.xlsx`,
      title: "Reading Mismatch Report",
      subtitle: `Period: ${fromDate} to ${toDate}  |  Mismatches: ${visible.length}`,
      sheetName: "Mismatches",
      columns: [
        { header: "Date", key: "date", width: 12 },
        { header: "Equipment", key: "equipment_name", width: 24 },
        { header: "Reg/ID", key: "equipment_reg", width: 14 },
        { header: "Log KM Close", key: "log_km", width: 14, numFmt: "#,##0.0" },
        { header: "Fuel KM Close", key: "fuel_km", width: 14, numFmt: "#,##0.0" },
        { header: "KM Diff", key: "km_diff", width: 12, numFmt: "#,##0.0", total: true },
        { header: "Log Hr Close", key: "log_hr", width: 14, numFmt: "#,##0.0" },
        { header: "Fuel Hr Close", key: "fuel_hr", width: 14, numFmt: "#,##0.0" },
        { header: "Hr Diff", key: "hr_diff", width: 12, numFmt: "#,##0.0", total: true },
        { header: "Fuel Issue No.", key: "fuel_issue_number", width: 16 }
      ],
      rows: visible
    });
    toast.success("Mismatch report exported");
  };

  const fieldCls = "px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono";

  return (
    <div className="space-y-4" data-testid="mismatch-report-tab">
      <div className="stat-card">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">From Date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={fieldCls} data-testid="mismatch-from-date" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">To Date</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={fieldCls} data-testid="mismatch-to-date" />
          </div>
          <div className="min-w-0 w-full sm:w-auto sm:min-w-[300px]">
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">Machine</label>
            <select value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)} className={`${fieldCls} w-full font-body`} data-testid="mismatch-machine-filter">
              <option value="">All machines ({mismatches.length})</option>
              {sortedEquipment.map(eq => {
                const n = mismatches.filter(m => m.equipment_id === eq.id).length;
                return <option key={eq.id} value={eq.id}>{eq.name?.trim()} — {eq.equipment_id?.trim()}{eq.equipment_type ? ` (${eq.equipment_type})` : ""}{n ? ` • ${n} mismatch` : ""}</option>;
              })}
            </select>
          </div>
          <button onClick={fetchMismatches} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="mismatch-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Loading..." : "Refresh"}
          </button>
          <button onClick={handleExport} disabled={visible.length === 0} className="btn-outline flex items-center gap-2" data-testid="mismatch-export-btn">
            <Download className="w-4 h-4" /> Export Excel
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            <strong data-testid="mismatch-count">{visible.length}</strong> mismatch(es) found where Daily Log closing readings differ from Fuel Issue readings on the same date.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mismatch-table">
            <thead>
              <tr className="border-b-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-600">
                <th className="py-2 px-3 text-left">Date</th>
                <th className="py-2 px-3 text-left">Equipment</th>
                <th className="py-2 px-3 text-right">Log KM Close</th>
                <th className="py-2 px-3 text-right">Fuel KM Close</th>
                <th className="py-2 px-3 text-right">KM Diff</th>
                <th className="py-2 px-3 text-right">Log Hr Close</th>
                <th className="py-2 px-3 text-right">Fuel Hr Close</th>
                <th className="py-2 px-3 text-right">Hr Diff</th>
                <th className="py-2 px-3 text-left">Fuel Issue</th>
                {isMasterAdmin && <th className="py-2 px-3 text-center">Fix</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((m, idx) => (
                <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`mismatch-row-${idx}`}>
                  <td className="py-2 px-3 font-mono">{m.date}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-primary">{m.equipment_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{m.equipment_reg}</div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{m.useKm ? m.log_km.toFixed(1) : <span className="text-[10px] text-slate-400">N/A</span>}</td>
                  <td className="py-2 px-3 text-right font-mono">{m.useKm ? m.fuel_km.toFixed(1) : <span className="text-[10px] text-slate-400">N/A</span>}</td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${m.km_diff > 0 ? "text-error" : "text-slate-400"}`}>{m.useKm ? m.km_diff.toFixed(1) : "N/A"}</td>
                  <td className="py-2 px-3 text-right font-mono">{m.useHr ? m.log_hr.toFixed(1) : <span className="text-[10px] text-slate-400">N/A</span>}</td>
                  <td className="py-2 px-3 text-right font-mono">{m.useHr ? m.fuel_hr.toFixed(1) : <span className="text-[10px] text-slate-400">N/A</span>}</td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${m.hr_diff > 0 ? "text-error" : "text-slate-400"}`}>{m.useHr ? m.hr_diff.toFixed(1) : "N/A"}</td>
                  <td className="py-2 px-3 font-mono text-warning">{m.fuel_issue_number}</td>
                  {isMasterAdmin && (
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => openEditDialog(m)} className="p-1 hover:bg-slate-200 rounded" title="Fix Mismatch" data-testid={`fix-mismatch-${idx}`}>
                        <Edit className="w-4 h-4 text-primary" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visible.length === 0 && !loading && (
          <div className="text-center py-8 text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-green-400" />
            No reading mismatches found for the selected period. All readings match!
          </div>
        )}
      </div>

      {/* Edit Mismatch Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="fix-mismatch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">FIX READING MISMATCH</DialogTitle>
          </DialogHeader>
          {editingMismatch && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-slate-100 rounded-sm">
                <p className="font-medium text-primary">{editingMismatch.equipment_name}</p>
                <p className="text-sm text-slate-600">{editingMismatch.equipment_reg} • {editingMismatch.date} • {editingMismatch.fuel_issue_number}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-xs font-medium text-blue-800 uppercase tracking-widest mb-3">Daily Log Readings</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] text-blue-600 mb-1">Closing KM</label>
                      <input type="number" step="0.1" value={editForm.log_km} onChange={(e) => setEditForm({...editForm, log_km: e.target.value})}
                        className="w-full px-3 py-2 border border-blue-300 rounded-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="fix-log-km" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-blue-600 mb-1">Closing Hour</label>
                      <input type="number" step="0.1" value={editForm.log_hr} onChange={(e) => setEditForm({...editForm, log_hr: e.target.value})}
                        className="w-full px-3 py-2 border border-blue-300 rounded-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="fix-log-hr" />
                    </div>
                  </div>
                  <button type="button" onClick={copyFuelToLog} className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1">
                    ← Copy from Fuel
                  </button>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <p className="text-xs font-medium text-amber-800 uppercase tracking-widest mb-3">Fuel Issue Readings</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] text-amber-600 mb-1">Closing KM</label>
                      <input type="number" step="0.1" value={editForm.fuel_km} onChange={(e) => setEditForm({...editForm, fuel_km: e.target.value})}
                        className="w-full px-3 py-2 border border-amber-300 rounded-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid="fix-fuel-km" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-amber-600 mb-1">Closing Hour</label>
                      <input type="number" step="0.1" value={editForm.fuel_hr} onChange={(e) => setEditForm({...editForm, fuel_hr: e.target.value})}
                        className="w-full px-3 py-2 border border-amber-300 rounded-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid="fix-fuel-hr" />
                    </div>
                  </div>
                  <button type="button" onClick={copyLogToFuel} className="mt-2 text-xs text-amber-600 hover:underline flex items-center gap-1">
                    ← Copy from Log
                  </button>
                </div>
              </div>

              {(editForm.log_km === editForm.fuel_km && editForm.log_hr === editForm.fuel_hr) && (
                <div className="p-2 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Readings now match!
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditDialogOpen(false)} className="btn-outline flex items-center gap-1">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button onClick={handleSaveFix} disabled={saving} className="btn-primary flex items-center gap-1" data-testid="fix-save-btn">
                  <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save Both"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MismatchReportTab;
